import { createAction, createAsyncThunk } from "@reduxjs/toolkit";

import {
  GET_SECTION_MANAGEMENT_SECTIONS_WEEK,
  ADD_SECTION,
  UPDATE_SECTION,
  REMOVE_SECTION,
} from "../../consts/apis";
import {
  SUCCESS_FETCH_DEPOT_SECTIONS,
  ERROR_FETCH_DEPOT_SECTIONS,
} from "../../consts/messages";
import { ACTIVE, ALL } from "../../consts/sectionDetails";

import api from "../../services/api";

import { syncToAsync } from "../../utils/async";
import { generateSectionColors } from "../../utils/sections";

import { addNotification, setSelectedSection } from "../ownApp";
import {
  editSectionsSelector,
  sectionEditSelector,
  sectionSelector,
  sectionsSelector,
  sectionViewSelector,
} from "./selectors";
import { getSelectedDepot } from "../ownApp/selectors";

export const resetSectionManagement = createAsyncThunk(
  "sectionManagement/resetSectionManagement",
  async (params) => ({})
);

export const setSectionView = createAction("sectionManagement/setSectionView");

export const fetchDepotSectionWeek = createAsyncThunk(
  "sectionManagement/fetchDepotSectionWeek",
  async ({ depotCode }, { dispatch }) => {
    try {
      const {
        data: { sections },
      } = await api.get(GET_SECTION_MANAGEMENT_SECTIONS_WEEK(depotCode));

      dispatch(
        addNotification({
          message: SUCCESS_FETCH_DEPOT_SECTIONS,
          options: { variant: "success" },
        })
      );

      dispatch(setSelectedSection(null));

      return generateSectionColors(sections);
    } catch (error) {
      dispatch(
        addNotification({
          message: ERROR_FETCH_DEPOT_SECTIONS,
          options: { variant: "error" },
        })
      );
      throw error;
    }
  }
);

export const editSection = createAsyncThunk(
  "sectionManagement/editSection",
  async ({ sectionCode }, { dispatch, getState }) => {
    const state = getState();
    const section = sectionSelector(sectionCode)(state);
    const sections = sectionsSelector(state);
    dispatch(setSelectedSection(null));
    return {
      section,
      sections: [...sections],
    };
  }
);

export const updateSectionEdit = createAsyncThunk(
  "sectionManagement/updateSectionEdit",
  async ({ name, value }, { getState }) => {
    const state = getState();
    const sectionEdit = sectionEditSelector(state);
    return {
      ...sectionEdit,
      [name]: value,
    };
  }
);

export const setSectionAsDefault = createAsyncThunk(
  "sectionManagement/setSectionAsDefault",
  async (params, { getState }) => {
    const state = getState();
    const sectionEdit = sectionEditSelector(state);
    const editSections = editSectionsSelector(state);
    return {
      sectionEdit: {
        ...sectionEdit,
        defaultSection: true,
      },
      editSections: editSections.map((section) => ({
        ...section,
        defaultSection: section.sectionCode === sectionEdit.sectionCode,
      })),
    };
  }
);

export const addSection = createAsyncThunk(
  "sectionManagement/addSection",
  async ({ sectionName }, { dispatch, getState }) => {
    const state = getState();
    const depotCode = getSelectedDepot(state);
    const sections = sectionsSelector(state);

    try {
      const { data } = await api.post(ADD_SECTION(depotCode), null, {
        sectionName,
      });
      if (!data.success) throw new Error("Failed to add new section");

      dispatch(
        addNotification({
          message: `Successfully saved new section.`,
          options: { variant: "success" },
        })
      );

      const { section: newSection } = data;

      syncToAsync(() => {
        const sectionView = sectionViewSelector(getState());
        if (sectionView === ACTIVE) dispatch(setSectionView(ALL));
        dispatch(setSelectedSection(newSection));
      });

      return [...sections, newSection];
    } catch (error) {
      dispatch(
        addNotification({
          message: error.message,
          options: { variant: "error" },
        })
      );
      throw error;
    }
  }
);

export const saveSection = createAsyncThunk(
  "sectionManagement/saveSection",
  async (params, { dispatch, getState }) => {
    const state = getState();
    const depotCode = getSelectedDepot(state);
    const sectionEdit = sectionEditSelector(state);
    const editSections = editSectionsSelector(state);

    const { sectionCode, sectionName, defaultSection } = sectionEdit;
    try {
      const { data } = await api.put(UPDATE_SECTION(depotCode), null, {
        sectionCode,
        sectionName,
        defaultSection,
      });
      if (!data.success) throw new Error("Failed to update section");

      dispatch(
        addNotification({
          message: `Successfully updated section.`,
          options: { variant: "success" },
        })
      );

      return editSections.map((section) => {
        if (section.sectionCode === sectionEdit.sectionCode) return sectionEdit;
        else return section;
      });
    } catch (error) {
      dispatch(
        addNotification({
          message: error.message,
          options: { variant: "error" },
        })
      );
      throw error;
    }
  }
);

export const removeSection = createAsyncThunk(
  "sectionManagement/removeSection",
  async ({ sectionCode }, { dispatch, getState }) => {
    const state = getState();
    const depotCode = getSelectedDepot(state);
    const sections = sectionsSelector(state);

    try {
      const { data } = await api.delete(REMOVE_SECTION(depotCode), null, {
        sectionCode,
      });
      if (!data.success) throw new Error("Failed to remove section");

      dispatch(
        addNotification({
          message: `Successfully removed section.`,
          options: { variant: "success" },
        })
      );

      return sections.filter((section) => section.sectionCode !== sectionCode);
    } catch (error) {
      dispatch(
        addNotification({
          message: error.message,
          options: { variant: "error" },
        })
      );
      throw error;
    } finally {
      dispatch(cancelSectionEdit());
      dispatch(setSelectedSection(null));
    }
  }
);

export const cancelSectionEdit = createAction(
  "sectionManagement/cancelSectionEdit"
);

export const setIsSectionEditValid = createAction(
  "sectionManagement/setIsSectionEditValid"
);
